import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ColumnHeaderTooltip from '../components/ColumnHeaderTooltip';
import { TableColumn } from '../types';
import { SortOrder } from 'antd/es/table/interface';
import { renderCell } from '../utils/renderCellHandler';
import { NonTranslatableText } from '@/shared/ui/NonTranslatableText';

const wrapNonTranslatableCellContent = (content: React.ReactNode) => {
	if (content === null || content === undefined || content === false) {
		return content;
	}

	if (typeof content === 'string' || typeof content === 'number') {
		return <NonTranslatableText>{content}</NonTranslatableText>;
	}

	return content;
};

/**
 * Hook that transforms raw Ant Design table column definitions into styled columns.
 *
 * Responsibilities:
 * - Localizes column titles with i18n, and swaps titles when editing/adding if
 *   `editableTitle` is provided in column config.
 * - Wraps titles with a consistent text style (via `titleClass`).
 * - Controls sorting UI:
 *    - When `isPrioritizing` is true, removes sort-related props to disable caret
 *      and header click sorting (filters remain intact).
 *    - Otherwise, applies the current sort indicator (`ascend` / `descend`) to the
 *      active column defined by `sortBy` and `direction`.
 *
 * @param columns        Original AntD column definitions.
 * @param sortBy         Column key currently sorted by.
 * @param direction      Sort direction: 'asc', 'desc', or null.
 * @param namespace      i18n namespace for translating column titles.
 * @param titleClass     CSS class applied to column titles.
 * @param isEditing      Whether the table is in row-editing mode (affects titles).
 * @param isAdding       Whether the table is in row-adding mode (affects titles).
 * @param isPrioritizing Whether the table is in prioritization mode (hides sorting UI).
 *
 * @returns Styled column definitions ready to pass to AntD's <Table />.
 */

export function useStyledColumns({
	columns,
	sortBy,
	direction,
	namespace = 'dynamic_table.columns',
	titleClass = 'text-tertiary font-greed-regular font-normal',
	isEditing = false,
	isAdding = false,
	isPrioritizing = false,
}: {
	columns: TableColumn[];
	sortBy?: string;
	direction?: 'asc' | 'desc' | null;
	namespace?: string;
	titleClass?: string;
	isEditing?: boolean;
	isAdding?: boolean;
	isPrioritizing?: boolean;
}): TableColumn[] {
	const { t } = useTranslation();

	const toSortOrder = (dir?: 'asc' | 'desc' | null): SortOrder | undefined => {
		if (!dir) return undefined;
		return dir === 'asc' ? 'ascend' : 'descend';
	};

	const filteredColumns = columns.filter((col) => !col.hidden);

	return useMemo(() => {
		return filteredColumns.map((col) => {
			const baseTitle = typeof col.title === 'string' ? t(`${namespace}.${col.title}`) : col.title;

			const finalTitle =
				(isEditing || isAdding) &&
				col.editTableConfig?.editableTitle &&
				typeof col.editTableConfig.editableTitle === 'string'
					? t(`${namespace}.${col.editTableConfig.editableTitle}`)
					: baseTitle;

			const wrappedRender = col.render
				? (value: any, record: any, index: number) =>
						renderCell(value, (v) => {
							const renderedContent = col.render!(v, record, index) as React.ReactNode;
							return col.preventAutoTranslate
								? wrapNonTranslatableCellContent(renderedContent)
								: renderedContent;
						})
				: (value: any) => {
						const renderedContent = renderCell(value);
						return col.preventAutoTranslate
							? wrapNonTranslatableCellContent(renderedContent)
							: renderedContent;
					};

			const base: TableColumn = {
				...col,
				title: (
					<span className={`${titleClass} inline-flex items-center gap-1`}>
						{col.headerTooltip && <ColumnHeaderTooltip tooltipText={t(col.headerTooltip)} />}
						<span>{finalTitle as string}</span>
					</span>
				),
				render: wrappedRender,
			};

			if (isPrioritizing) {
				const { sorter, sortOrder, defaultSortOrder, sortDirections, sortIcon, ...noSort } = base;
				return noSort as TableColumn;
			}

			const isSortedCol = col.dataIndex === sortBy;
			const sortOrderValue: SortOrder | undefined = isSortedCol
				? toSortOrder(direction)
				: undefined;

			return {
				...base,
				...(col.sorter ? { sorter: col.sorter } : {}),
				...(sortOrderValue ? { sortOrder: sortOrderValue } : {}),
			};
		});
	}, [columns, sortBy, direction, isEditing, t, namespace, titleClass, isAdding, isPrioritizing]);
}
